import { useCallback, useEffect, useMemo, useState, Fragment } from "react";
import { useCan } from "../../hooks/useAreaLevel";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "../../components/Layout";
import Person360Overlay from "../../components/Person360Overlay";
import api from "../../lib/api";
import { resolveCrmPersonId } from "../../lib/personResolve";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { useAccountManagers, amMatchesStaff } from "../../hooks/useAccountManagers";
import { OwnerChip } from "../../components/OwnerChip";
import { InitialBadge } from "./_AssigneeStack";
import ActivityCell from "../../components/activity/ActivityCell";
import useActivitySummaries from "../../components/activity/useActivitySummaries";
import { toast } from "sonner";
import {Banknote, CheckCircle2 as CheckIcon, ArchiveRestore} from "lucide-react";
import QuoteBinDrawer from "./panels/QuoteBinDrawer";
import DuplicateQuotesDrawer from "./panels/DuplicateQuotesDrawer";
import DateRangeFilter from "../../components/DateRangeFilter";
import { EMPTY_DATE_FILTER, normalizeDateFilter, isDateFilterActive, resolveDateFilter } from "../../lib/dateRange";

const QUOTE_DATE_FIELDS = [
  { value: "created", label: "Created" },
  { value: "updated", label: "Last updated" },
  { value: "expiry", label: "Expiry" },
  { value: "target", label: "Target date" },
];
import { ProformaMarkButton } from "./panels/ProformaIssue";
import { DELIVERY_BANDS, urgencyBand, quoteCompletion, completionTone } from "./_quoteHelpers";
import { suggestVehicle } from "./panels/_buildTypeContent";
import { canonicalStatus, docClass as sharedDocClass, QuoteStatusMenu, statusChangePatch, QUOTE_STATUSES } from "./quoteStatus";
import MultiSelectDropdown from "../../components/dev-tracker/MultiSelectDropdown";
import SavedViewChips, { useSavedViews } from "../../components/SavedViewChips";
import {
  RefreshCw, Plus, Search, ChevronRight, ChevronDown, ExternalLink, AlertTriangle,
  Package, StickyNote, Paperclip, Truck, Crown, Copy, Archive, FileText, Loader2, SquarePen, MapPin,
} from "lucide-react";

const API = "";
const money = (n) => `£${Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ── Money + margin are NOT stored on the quote — compute them from the line
// items (unit sell × qty) and the supplier rebate %. Post-rebate margin matches
// Quote Builder's headline figure (rebate reduces cost → lifts margin).
const lineTotal = (l) => (Number(l.qty) || 0) * (Number(l.unitPrice) || 0);
const lineMarginPct = (l) => {
  const sell = Number(l.unitPrice) || 0;
  if (sell <= 0) return null;
  return ((sell - (Number(l.cost) || 0)) / sell) * 100;
};
function quoteMargin(q) {
  const items = q.LineItems || [];
  const revenue = items.reduce((s, l) => s + lineTotal(l), 0);
  const grossCost = items.reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.cost) || 0), 0);
  const rebatePct = Number(q.RebatePct) || 0;
  const rebateAmt = grossCost * (rebatePct / 100);
  const netCost = grossCost - rebateAmt;
  const marginAbs = revenue - netCost;
  const marginPct = revenue > 0 ? (marginAbs / revenue) * 100 : null;
  return { revenue, grossCost, netCost, rebateAmt, rebatePct, marginAbs, marginPct };
}
// System snapshots vs human staff notes.
const SNAP_PREFIX = "QUOTE SUMMARY SNAPSHOT";
const isSnapshot = (n) => String((n && (n.body || n.text)) || "").trim().startsWith(SNAP_PREFIX);
// Delivery & site checklist — ONLY the items actually set in Quote Builder.
function deliveryChecklist(q) {
  const d = q.Delivery || {};
  const t = q.Timeline || {};
  const out = [];
  if (d.method) {
    const band = DELIVERY_BANDS.find((b) => b.key === d.band);
    out.push(`Delivery: ${d.method}${band ? ` · ${band.label}` : ""}${d.cost ? ` · ${money(d.cost)}` : ""}`);
  }
  if (d.kerbsideOnly) out.push("Kerbside drop only");
  if (d.forkliftAvailable) out.push("Forklift available on site");
  if (Number(d.peopleOnSite) > 0) out.push(`${d.peopleOnSite} ${Number(d.peopleOnSite) === 1 ? "person" : "people"} on site`);
  if (d.parkingRestricted) out.push("Parking restricted");
  if (d.parking) out.push(`Parking: ${d.parking}`);
  if (d.siteContactName) out.push(`Site contact: ${d.siteContactName}`);
  if (d.notes) out.push(`Access notes: ${d.notes}`);
  const veh = suggestVehicle(d.estimatedWeightKg);
  if (veh) out.push(`${Number(d.estimatedWeightKg).toLocaleString("en-GB")}kg → ${veh.vehicle}`);
  if (t.asap) out.push("Target: ASAP");
  else if (t.targetDate) out.push(`Target: ${new Date(t.targetDate).toLocaleDateString("en-GB")}`);
  if (Number(t.desperation) > 0) out.push(`Urgency ${t.desperation}/10`);
  return out;
}
const initials = (s) => (s || "").split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "—";
// Normalise a quote's account managers → array of names (supports single
// AccountManager string + an Assignees[] array of strings or {name} objects).
function amNames(q) {
  const out = [];
  const push = (v) => {
    const n = typeof v === "string" ? v : (v && typeof v === "object" ? v.name : null);
    if (n && !out.includes(n)) out.push(n);
  };
  push(q.AccountManager);
  (q.Assignees || []).forEach(push);
  return out;
}
// The single account-manager name (owner) as a plain string.
const ownerName = (q) => (typeof q.AccountManager === "string" ? q.AccountManager : q.AccountManager?.name || "");
// Collaborator names = everyone on the quote EXCEPT the account manager (owner).
function collabNames(q) {
  const owner = ownerName(q).toLowerCase();
  const out = [];
  (q.Assignees || []).forEach((a) => {
    const n = typeof a === "string" ? a : a?.name;
    if (n && n.toLowerCase() !== owner && !out.includes(n)) out.push(n);
  });
  return out;
}
// "I'm collaborating" = I'm on the quote's assignees but I'm NOT the owner/AM.
const isMyCollab = (q, me) => {
  const m = (me || "").toLowerCase();
  if (!m) return false;
  return ownerName(q).toLowerCase() !== m && collabNames(q).some((n) => n.toLowerCase() === m);
};

const TIER_PILL = {
  Black:    "bg-slate-900 text-slate-100 border-slate-500/40",
  Platinum: "bg-slate-200/10 text-slate-100 border-slate-300/40",
  Gold:     "bg-amber-500/15 text-amber-300 border-amber-500/40",
  Silver:   "bg-slate-400/15 text-slate-200 border-slate-400/40",
  Bronze:   "bg-orange-800/20 text-orange-300 border-orange-700/40",
};
// Quote status is unified onto ONE canonical value — see ./quoteStatus. These
// thin wrappers keep the existing call sites (docState / docClass) working.
const docState = (s, opts) => canonicalStatus(s, opts);
const docClass = (s, opts) => sharedDocClass(s, opts);
const _TIERS = ["black", "platinum", "gold", "silver", "bronze"];
// Deal-pipeline stage accent tokens → pill classes (mirrors crm_pipeline_config colors).
const STAGE_PILL = {
  slate:   "bg-slate-500/15 text-slate-300 border-slate-500/30",
  fuchsia: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30",
  sky:     "bg-sky-500/15 text-sky-300 border-sky-500/30",
  purple:  "bg-purple-500/15 text-purple-300 border-purple-500/30",
  orange:  "bg-orange-500/15 text-orange-300 border-orange-500/30",
  amber:   "bg-amber-500/15 text-amber-300 border-amber-500/30",
  yellow:  "bg-yellow-500/15 text-yellow-300 border-yellow-500/30",
  emerald: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  rose:    "bg-rose-500/15 text-rose-300 border-rose-500/30",
  cyan:    "bg-cyan-500/15 text-cyan-300 border-cyan-500/30",
};
const stageClass = (color) => STAGE_PILL[color] || STAGE_PILL.slate;
// ── Status ↔ deal-stage auto-mapping (advance-only; never regresses) ──
// Rank gates the "never regress" rule — a move only applies if it doesn't drop rank.
const STATUS_RANK = { Draft: 0, Sent: 1, Accepted: 2, Paid: 3, Rejected: 4, Expired: 4 };
const STAGE_TO_STATUS = { quote_sent: "Sent", waiting_payment: "Accepted", won_ordered: "Paid", lost_archive: "Rejected" };
// Customer type (lead vs existing customer) — single colour per pipeline so the
// Type chip + Deal-Stage edge instantly signal which workflow a quote is in.
const TYPE_PILL = {
  lead: "bg-violet-500/15 text-violet-300 border-violet-500/40",
  deal: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
};
const typeMeta = (route) =>
  route === "lead" ? { label: "LEAD",     cls: TYPE_PILL.lead, edge: "rgba(167,139,250,0.85)", prefix: "L" }
  : route === "deal" ? { label: "CUSTOMER", cls: TYPE_PILL.deal, edge: "rgba(52,211,153,0.85)", prefix: "D" }
  : null;
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? "—" : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};
function tierClass(tier) {
  const t = String(tier || "").toLowerCase();
  for (const k of _TIERS) if (t.includes(k)) return TIER_PILL[k[0].toUpperCase() + k.slice(1)];
  return "border-white/10 text-slate-400";
}
function tierShort(tier) {
  const t = String(tier || "").toLowerCase();
  for (const k of _TIERS) if (t.includes(k)) return k[0].toUpperCase() + k.slice(1);
  return tier;
}

function relTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso); if (isNaN(d)) return "—";
  const s = (Date.now() - d.getTime()) / 1000;
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 172800) return "1d";
  return `${Math.floor(s / 86400)}d`;
}
function expiry(iso, status) {
  if (String(status).toLowerCase() === "accepted") return { label: "won", cls: "text-emerald-300" };
  if (!iso) return { label: "—", cls: "text-slate-500" };
  const days = Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
  if (isNaN(days)) return { label: "—", cls: "text-slate-500" };
  if (days < 0) return { label: "expired", cls: "text-rose-400 font-semibold" };
  if (days <= 2) return { label: `${days}d`, cls: "text-rose-400 font-semibold" };
  if (days <= 7) return { label: `${days}d`, cls: "text-amber-300" };
  return { label: `${days}d`, cls: "text-slate-300" };
}
const custKey = (q) => String(q.Customer?.personId || q.Customer?.PersonId || (q.Customer?.email || "").toLowerCase() || "");

// hover popover wrapper
function SortTh({ k, label, align = "left", colSort, onColSort }) {
  const active = colSort && colSort.key === k;
  const arrow = active ? (colSort.dir === "asc" ? "↑" : "↓") : "↕";
  return (
    <th className={`px-2 py-2 select-none cursor-pointer whitespace-nowrap hover:text-slate-300 ${align === "right" ? "text-right" : "text-left"}`}
      onClick={() => onColSort(k)} data-testid={`view-quotes-sortcol-${k}`}>
      {label} <span className={active ? "text-orange-300" : "text-slate-600"}>{arrow}</span>
    </th>
  );
}

function InfoGlyph({ icon: Icon, count, tone, children, testid }) {
  const active = count > 0;
  return (
    <span className="relative group inline-flex items-center" data-testid={testid}>
      <Icon className={`w-3.5 h-3.5 ${active ? tone : "text-slate-700"}`} />
      {active && <span className="text-[10px] font-mono ml-0.5 text-slate-400 tabular-nums">{count}</span>}
      {active && children && (
        <span className="pointer-events-none absolute left-0 top-5 z-30 hidden group-hover:block w-64 rounded-lg border border-white/15 bg-[#0B111C] p-2.5 shadow-2xl text-left">
          {children}
        </span>
      )}
    </span>
  );
}

// ── Filter registry buckets (Task Module 05c) ──────────────────────────
const me_id = () => { try { return JSON.parse(localStorage.getItem("user") || "{}"); } catch { return {}; } };
const isPaidQ = (q) => canonicalStatus(q.Status, { payment: q.Payment, sent: q.Sent }) === "Paid";
const urgencyBucketOf = (q) => urgencyBand(q.Timeline?.desperation, isPaidQ(q)).key;
const URGENCY_OPTS = [
  { value: "paid", label: "Paid", colour: "#34d399" },
  { value: "chill", label: "Chill 0–1", colour: "#64748b" },
  { value: "watching", label: "Watching 2–4", colour: "#fb923c" },
  { value: "high", label: "High 5–7", colour: "#f59e0b" },
  { value: "critical", label: "Critical 8–10", colour: "#ef4444" },
];
const marginBandOf = (q) => {
  const m = quoteMargin(q).marginPct;
  if (m == null) return "unknown";
  return m < 0 ? "negative" : m < 10 ? "thin" : "healthy";
};
const MARGIN_OPTS = [
  { value: "healthy", label: "Healthy (≥10%)", colour: "#34d399" },
  { value: "thin", label: "Thin (0–10%)", colour: "#fbbf24" },
  { value: "negative", label: "Negative (<0%)", colour: "#f87171" },
];
const expiryBucketOf = (q) => {
  if (!q.ExpiryDate) return "none";
  const d = Math.ceil((new Date(q.ExpiryDate).getTime() - Date.now()) / 86400000);
  return d < 0 ? "expired" : d <= 3 ? "soon" : "live";
};
const EXPIRY_OPTS = [
  { value: "live", label: "Live" },
  { value: "soon", label: "Expiring soon (≤3d)", colour: "#fbbf24" },
  { value: "expired", label: "Expired", colour: "#f87171" },
];
const targetBucketOf = (q) => {
  const t = q.Timeline?.targetDate;
  if (!t) return "none";
  const d = Math.ceil((new Date(t).getTime() - Date.now()) / 86400000);
  if (isNaN(d)) return "none";
  return d < 0 ? "overdue" : d <= 7 ? "this_week" : d <= 14 ? "next_week" : "later";
};
const TARGET_OPTS = [
  { value: "overdue", label: "Overdue", colour: "#f87171" },
  { value: "this_week", label: "This week", colour: "#fb923c" },
  { value: "next_week", label: "Next week", colour: "#f59e0b" },
  { value: "later", label: "Later" },
  { value: "none", label: "No target", colour: "#64748b" },
];
const FLAG_OPTS = [
  { value: "dup", label: "Possible duplicates", colour: "#e879f9" },
  { value: "no_customer", label: "No customer", colour: "#fbbf24" },
  { value: "asap", label: "ASAP", colour: "#fb7185" },
  { value: "attachments", label: "Has attachments", colour: "#c084fc" },
  { value: "notes", label: "Has staff notes", colour: "#94a3b8" },
];
const flagMatch = (q, f) => (
  f === "dup" ? (q._dup_count || 0) > 0
  : f === "no_customer" ? !!q._no_customer
  : f === "asap" ? !!q.Timeline?.asap
  : f === "attachments" ? (q.Attachments || []).length > 0
  : f === "notes" ? (q.StaffNotes || []).length > 0
  : false
);
const statusMatch = (q, s) => canonicalStatus(q.Status, { payment: q.Payment, sent: q.Sent }) === s;
const STATUS_VALUES = ["Draft", "Sent", "Accepted", "Rejected", "Paid", "Expired"];

// Focus/chase buckets — SAME definitions as Deal Pipeline (routes/deals.py
// _passes_focus). Per-row match comes from the backend `_focus` tag so the
// thresholds never drift from the pipeline.
const FOCUS_OPTS = [
  { value: "overdue",  label: "🔴 Overdue target" },
  { value: "due_soon", label: "⏰ Due ≤7 days" },
  { value: "expiring", label: "⌛ Expiring ≤7 days" },
  { value: "expired",  label: "💀 Expired" },
  { value: "stale",    label: "🕸 Stale — chase (30d+)" },
  { value: "sla_overdue", label: "🚦 SLA overdue (48h)" },
];
// Row chips — only "alarming" chase states get a visible badge. "Expired" is
// intentionally NOT shown here: it's a quote STATUS now (manual), and the date
// countdown still lives in the Expires column, so a Draft can't look "Expired".
const FOCUS_BADGE = {
  overdue: { label: "Overdue", cls: "bg-rose-500/20 text-rose-300 border-rose-500/40" },
  stale:   { label: "Stale",   cls: "bg-amber-500/20 text-amber-300 border-amber-500/40" },
};

export default function ViewQuotes() {
  const can = useCan("/quotes");
  const { user } = useAuth();
  const { theme } = useTheme();
  const managers = useAccountManagers();
  const canMargin = true; // Margin is visible to every user (owner decision, Jun 2026)

  const [raw, setRaw] = useState([]);
  const [noCust, setNoCust] = useState([]);
  const [facets, setFacets] = useState({ stages: {}, tiers: {}, assignees: {}, focus: {}, type: {} });
  const [openValue, setOpenValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dealStage, setDealStage] = useState([]);  // deal-pipeline stage filter (multi) — the sales workflow
  const [typeF, setTypeF] = useState("");          // customer type: "" | lead | deal | no_deal
  const [tier, setTier] = useState([]);        // multi
  const [docStatus, setDocStatus] = useState([]);   // quote document status (multi)
  const [buildType, setBuildType] = useState([]);   // ⭐ quote builder Build type
  const [urgencyB, setUrgencyB] = useState([]);     // ⭐ urgency bucket
  const [supplierF, setSupplierF] = useState([]);   // supplier
  const [deliveryF, setDeliveryF] = useState([]);   // delivery method
  const [marginF, setMarginF] = useState([]);       // margin band (role-gated)
  const [expiryF, setExpiryF] = useState([]);       // expiry bucket
  const [dateF, setDateF] = useState(EMPTY_DATE_FILTER);
  const [dupesOpen, setDupesOpen] = useState(false);
  const [targetF, setTargetF] = useState([]);       // ⭐ target date bucket
  const [flags, setFlags] = useState([]);           // boolean flags (dup / no-customer / asap / attachments / notes)
  const [scope, setScope] = useState("all");        // all | mine | created
  const [sort, setSort] = useState("created");
  const [am, setAm] = useState([]);            // account-manager filter (multi)
  const [focus, setFocus] = useState("");      // overdue|due_soon|expiring|expired|stale|sla_overdue (single) — Deal Pipeline parity
  const [colSort, setColSort] = useState(null); // { key, dir } — column header sort
  const [expanded, setExpanded] = useState(() => new Set());
  const [alerts, setAlerts] = useState({});

  // Unread quote-activity flags for me (AM/collaborator) — flashing exclamation
  // in the Info column with a hover of what changed. Quiet 30s refresh.
  useEffect(() => {
    let alive = true;
    const fetchAlerts = async () => {
      try { const { data } = await api.get("/notifications/by-quote"); if (alive) setAlerts(data || {}); }
      catch { /* non-fatal */ }
    };
    fetchAlerts();
    const t = setInterval(fetchAlerts, 30000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  // Expanded-panel per-row UI state (keyed by quote id).
  const [dupOpen, setDupOpen] = useState({});   // {id: bool} — duplicates sub-list open
  const [dupData, setDupData] = useState({});   // {id: [siblings]}
  const [dupSel, setDupSel] = useState({});     // {id: Set(siblingIds)}
  const [dupBusy, setDupBusy] = useState({});   // {id: bool} — archiving in progress
  const [sysLog, setSysLog] = useState({});     // {id: bool} — show system snapshot log
  const [binOpen, setBinOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`${API}/quotes?limit=200&sort=updated`);
      const pages = Math.ceil((data.total || 0) / 200);
      const more = pages > 1 ? await Promise.all(Array.from({ length: pages - 1 }, (_, i) => api.get(`${API}/quotes?limit=200&sort=updated&page=${i + 2}`))) : [];
      const seen = new Set();
      const items = [...(data.items || []), ...more.flatMap((r) => r.data.items || [])].filter((x) => !seen.has(x._id) && seen.add(x._id));
      setRaw(items);
      setNoCust(data.no_customer || []);
      setFacets(data.facets || { stages: {}, tiers: {}, assignees: {}, focus: {} });
      setOpenValue(items
        .filter((q) => !["won", "lost", "reject", "accept", "paid", "ordered", "expire", "archiv", "frozen"]
          .some((k) => String(q.Status).toLowerCase().includes(k)))
        .reduce((s, q) => s + Number(q.Totals?.total || 0), 0));
    } catch {
      toast.error("Failed to load quotes");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Pre-filter from the URL (e.g. My Dashboard "SLA overdue" tile → ?focus=sla_overdue).
  const [searchParams] = useSearchParams();
  useEffect(() => { if (searchParams.get("bin") !== null) setBinOpen(true); }, [searchParams]);
  useEffect(() => {
    const f = searchParams.get("focus");
    if (f) setFocus(f);
  }, [searchParams]);

  const meLabel = user?.name || user?.email || "staff";
  const patchQuote = async (q, payload) => {
    const qid = q._id;
    try {
      const { data } = await api.patch(`${API}/quotes/${qid}`, payload);
      const ss = data?._stage_sync;
      setRaw((prev) => prev.map((x) => (x._id === qid
        ? { ...x, ...payload, Timeline: data?.quote?.Timeline ?? x.Timeline, ...(ss ? { _deal_stage: ss } : {}) } : x)));
      if (ss) toast.success(`${ss.pipeline === "lead" ? "Lead" : "Deal"} stage → ${ss.label}`);
    } catch { toast.error("Save failed"); }
  };
  // Reset a quote's customer-facing validity (never locks — just refreshes the date).
  const extendQuote = async (q) => {
    try {
      const { data } = await api.post(`${API}/quotes/${q._id}/extend`);
      setRaw((prev) => prev.map((x) => (x._id === q._id ? { ...x, ExpiryDate: data.expiry } : x)));
      toast.success(`Validity extended to ${new Date(data.expiry).toLocaleDateString("en-GB")}`);
    } catch { toast.error("Could not extend validity"); }
  };
  // One unified status control — same values/colours as Quote Builder. Archived
  // routes through the archive endpoint (removes it from this active list).
  const changeStatus = async (q, next) => {
    if (next === docState(q.Status)) return;
    if (next === "Archived") {
      if (!window.confirm(`Archive ${q.QuoteRef}? It will be hidden from active lists and dashboards but can be restored later.`)) return;
      try {
        await api.post(`${API}/quotes/${q._id}/archive`, { reason: "Archived from View Quotes" });
        setRaw((prev) => prev.filter((x) => x._id !== q._id));
        toast.success(`${q.QuoteRef} archived`, { duration: 8000, action: { label: "Undo", onClick: () => undoArchive([q._id]) } });
      } catch { toast.error("Archive failed"); }
      return;
    }
    await patchQuote(q, statusChangePatch(next, q, q.AccountManager || meLabel));
    toast.success(`Status → ${next}`);
  };

  // Deal-stage options (deal + lead pipelines) for the inline expanded dropdown.
  const [dealStageOpts, setDealStageOpts] = useState([]);
  const [leadStageOpts, setLeadStageOpts] = useState([]);
  const [autoMap, setAutoMap] = useState(true);
  useEffect(() => {
    Promise.all([api.get(`${API}/crm/stages`), api.get(`${API}/crm/lead-stages`), api.get(`${API}/config/pipeline-automation`)])
      .then(([d, l, c]) => {
        setDealStageOpts((d.data.items || d.data.stages || []).map((s) => ({ key: s.key, label: s.label })));
        setLeadStageOpts((l.data.items || l.data.stages || []).map((s) => ({ key: s.key, label: s.label })));
        setAutoMap((c.data?.quote?.automap_enabled ?? true));
      })
      .catch(() => {});
  }, []);
  // Move the linked customer's DEAL stage from the quote row (deal pipeline only;
  // lead-pipeline stages are moved on the Lead Pipeline board). Stage lives at the
  // customer-360 level so the quote's _deal_stage reflects it after refresh.
  const changeDealStage = async (q, key) => {
    const pid = q._customer_summary?.person_id;
    if (!pid || !key || q._deal_stage?.pipeline === "lead") return;
    const picked = dealStageOpts.find((o) => o.key === key);
    try {
      await api.patch(`${API}/deals/${pid}/stage`, { stage_key: key });
      setRaw((prev) => prev.map((x) => x._id === q._id
        ? { ...x, _deal_stage: { ...(x._deal_stage || {}), key, label: picked?.label || key, pipeline: "deal" } }
        : x));
      toast.success(`Deal stage → ${picked?.label || key}`);
      // Reverse map (stage → status): SUGGEST, advance-only.
      const nextStatus = STAGE_TO_STATUS[key];
      const curStatus = docState(q.Status, { payment: q.Payment, sent: q.Sent });
      if (autoMap && nextStatus && nextStatus !== curStatus
          && ((STATUS_RANK[nextStatus] ?? 0) > (STATUS_RANK[curStatus] ?? 0) || nextStatus === "Rejected")
          && window.confirm(`Also move this quote's status to "${nextStatus}" to match the deal stage?`)) {
        await patchQuote(q, statusChangePatch(nextStatus, q, q.AccountManager || meLabel));
        toast.success(`Status → ${nextStatus}`);
      }
    } catch { toast.error("Could not change deal stage"); }
  };

  // ── Duplicates sub-list: fetch the customer's OTHER quotes, tick + archive.
  const toggleDup = async (q) => {
    const qid = q._id;
    const willOpen = !dupOpen[qid];
    setDupOpen((p) => ({ ...p, [qid]: willOpen }));
    if (willOpen && !dupData[qid]) {
      try {
        const { data } = await api.get(`${API}/quotes/${qid}/siblings`);
        setDupData((p) => ({ ...p, [qid]: data.siblings || [] }));
      } catch { toast.error("Could not load the customer's other quotes"); }
    }
  };
  const toggleDupSel = (qid, sid) => setDupSel((p) => {
    const cur = new Set(p[qid] || []);
    cur.has(sid) ? cur.delete(sid) : cur.add(sid);
    return { ...p, [qid]: cur };
  });
  const undoArchive = async (ids) => {
    try { await Promise.all(ids.map((id) => api.post(`${API}/quotes/${id}/unarchive`))); toast.success("Restored"); load(); }
    catch { toast.error("Undo failed"); }
  };
  const archiveSelected = async (q) => {
    const qid = q._id;
    const sel = Array.from(dupSel[qid] || []);
    if (!sel.length) return;
    if (!window.confirm(`Archive ${sel.length} quote${sel.length > 1 ? "s" : ""}? They'll be hidden from active lists but can be restored later.`)) return;
    setDupBusy((p) => ({ ...p, [qid]: true }));
    try {
      await Promise.all(sel.map((sid) => api.post(`${API}/quotes/${sid}/archive`, { reason: `Duplicate of ${q.QuoteRef}` })));
      setRaw((prev) => prev.filter((x) => !sel.includes(x._id)));
      setDupData((p) => ({ ...p, [qid]: (p[qid] || []).filter((s) => !sel.includes(s.id)) }));
      setDupSel((p) => ({ ...p, [qid]: new Set() }));
      toast.success(`Archived ${sel.length} quote${sel.length > 1 ? "s" : ""}`, { duration: 8000, action: { label: "Undo", onClick: () => undoArchive(sel) } });
    } catch { toast.error("Archive failed"); }
    finally { setDupBusy((p) => ({ ...p, [qid]: false })); }
  };

  // Tier chips are data-driven from the backend facet counts; deal-stage chips are
  // derived client-side from the loaded quotes (the deal pipeline is now the sole
  // sales-workflow filter — quote status was retired as a pipeline).
  const tierFacet = useMemo(() => facets.tiers || {}, [facets]);
  const dealStageGroups = useMemo(() => {
    const m = {};
    raw.forEach((q) => {
      if (String(q.Status).toLowerCase() === "archived") return;
      const ds = q._deal_stage;
      const key = ds ? ds.key : "_none";
      if (!m[key]) m[key] = { key, label: ds ? ds.label : "No deal", color: ds ? ds.color : "slate", count: 0 };
      m[key].count += 1;
    });
    return Object.values(m).sort((a, b) => b.count - a.count);
  }, [raw]);
  // Group raw tier vocabularies (e.g. "Gold Tier", "GOLD - 2.5% & Cash Back") under
  // one short label so users see a single "Gold" chip that filters all variants.
  const tierGroups = useMemo(() => {
    const m = {};
    Object.entries(tierFacet).forEach(([raw, n]) => {
      const label = tierShort(raw);
      if (!m[label]) m[label] = { label, count: 0, keys: [] };
      m[label].count += n;
      m[label].keys.push(raw);
    });
    return Object.values(m).sort((a, b) => b.count - a.count);
  }, [tierFacet]);
  const dupTotal = useMemo(() => raw.filter((q) => (q._dup_count || 0) > 0).length, [raw]);
  const paidTotal = useMemo(() => raw.filter((q) => !!q.Payment?.paid).length, [raw]);
  const paidThisMonth = useMemo(() => {
    const now = new Date(); const m = now.getMonth(); const y = now.getFullYear();
    return raw.filter((q) => {
      if (!q.Payment?.paid) return false;
      const d = q.Payment?.markedAt ? new Date(q.Payment.markedAt) : null;
      return d && d.getMonth() === m && d.getFullYear() === y;
    }).length;
  }, [raw]);
  const sentTotal = useMemo(() => raw.filter((q) => !!q.Sent?.sent).length, [raw]);
  const assigneeList = useMemo(() => Object.entries(facets.assignees || {})
    .sort((a, b) => b[1] - a[1]).map(([k]) => k), [facets]);

  const updatedAt = (q) => q._updated_at || q._created_at;
  const createdAt = (q) => q._created_at || q.CreatedDate || q._updated_at;
  const custName = (q) => q.Customer?.name || q._customer_summary?.name || "";
  const custPostcode = (q) => q.Customer?.postCode || q.Customer?.postcode || q._customer_summary?.post_code || "";
  const sitePostcode = (q) => q.Delivery?.deliveryAddress?.postcode || custPostcode(q);
  const custTier = (q) => q.Customer?.tier || q._customer_summary?.tier || "";
  const colAccess = {
    ref: (q) => String(q.QuoteRef || "").toLowerCase(),
    customer: (q) => String(custName(q) || q.Customer?.company || q._customer_summary?.company || "").toLowerCase(),
    tier: (q) => String(tierShort(custTier(q)) || "").toLowerCase(),
    status: (q) => docState(q.Status).toLowerCase(),
    type: (q) => String(q._pipeline_type || "zzz"),
    dealstage: (q) => String(q._deal_stage?.label || "").toLowerCase(),
    value: (q) => Number(q.Totals?.total || 0),
    margin: (q) => Number(q.Totals?.marginPct ?? q.Totals?.margin ?? -Infinity),
    expires: (q) => new Date(q.ExpiryDate || 0).getTime(),
    am: (q) => (amNames(q)[0] || "").toLowerCase(),
    created: (q) => new Date(createdAt(q) || 0).getTime(),
    updated: (q) => new Date(updatedAt(q) || 0).getTime(),
    complete: (q) => quoteCompletion(q).pct,
  };
  const onColSort = (key) => setColSort((p) =>
    p && p.key === key ? { key, dir: p.dir === "asc" ? "desc" : "asc" } : { key, dir: ["value", "updated", "created", "expires", "margin", "complete"].includes(key) ? "desc" : "asc" });

  const rows = useMemo(() => {
    let r = raw.filter((q) => String(q.Status).toLowerCase() !== "archived");
    // Scope (All / Assigned to me / Created by me / Collaborating)
    if (scope !== "all") {
      const meName = (user?.name || "").toLowerCase();
      const meEmail = (user?.email || "").toLowerCase();
      r = r.filter((q) => {
        if (scope === "created") return String(q.CreatedBy || "").toLowerCase() === meName || String(q.CreatedBy || "").toLowerCase() === meEmail;
        if (scope === "collab") return isMyCollab(q, user?.name || user?.email);
        return amNames(q).some((n) => n.toLowerCase() === meName);
      });
    }
    // OR within a filter, AND across filters.
    if (typeF) r = r.filter((q) => (q._pipeline_type || "no_deal") === typeF);
    if (dealStage.length) r = r.filter((q) => dealStage.includes(q._deal_stage ? q._deal_stage.key : "_none"));
    if (tier.length) r = r.filter((q) => tier.includes(tierShort(custTier(q))));
    if (docStatus.length) r = r.filter((q) => docStatus.some((s) => statusMatch(q, s)));
    if (buildType.length) r = r.filter((q) => buildType.includes(q.BuildType || "—"));
    if (urgencyB.length) r = r.filter((q) => urgencyB.includes(urgencyBucketOf(q)));
    if (supplierF.length) r = r.filter((q) => supplierF.includes(q.Supplier?.name || "—"));
    if (deliveryF.length) r = r.filter((q) => deliveryF.includes(q.Delivery?.method || q.Delivery || "—"));
    if (canMargin && marginF.length) r = r.filter((q) => marginF.includes(marginBandOf(q)));
    if (expiryF.length) r = r.filter((q) => expiryF.includes(expiryBucketOf(q)));
    if (targetF.length) r = r.filter((q) => targetF.includes(targetBucketOf(q)));
    if (focus) r = r.filter((q) => focus === "sla_overdue" ? !!q.sla_breached : (q._focus || []).includes(focus));
    if (flags.length) r = r.filter((q) => flags.some((f) => flagMatch(q, f)));
    if (am.length) { const sel = managers.filter((m) => am.includes(m.email)); r = r.filter((q) => amMatchesStaff(amNames(q), sel)); }
    if (isDateFilterActive(dateF)) {
      const { from, to } = resolveDateFilter(dateF);
      const pickDate = { created: createdAt, updated: updatedAt, expiry: (q) => q.ExpiryDate, target: (q) => q.Timeline?.targetDate }[dateF.field || "created"];
      const f = from ? new Date(from).getTime() : -Infinity, t = to ? new Date(to).getTime() : Infinity;
      r = r.filter((q) => { const d = new Date(pickDate(q) || NaN).getTime(); return d >= f && d <= t; });
    }
    const n = search.trim().toLowerCase();
    if (n) r = r.filter((q) => [
      q.QuoteRef,
      custName(q), q.Customer?.company, q._customer_summary?.company,
      q.Customer?.email, q._customer_summary?.email,
      q.Customer?.phone, q._customer_summary?.phone,
      custPostcode(q), q.Customer?.city, q._customer_summary?.city,
      q.Supplier?.name, q.BuildType, q.ProjectRef,
      q.Delivery?.deliveryAddress?.postcode, q.Delivery?.deliveryAddress?.city, q.Delivery?.deliveryAddress?.line1,
      ...amNames(q),
    ].some((v) => String(v || "").toLowerCase().includes(n)));
    if (colSort) {
      const acc = colAccess[colSort.key];
      const dir = colSort.dir === "asc" ? 1 : -1;
      r = [...r].sort((a, b) => {
        const av = acc(a), bv = acc(b);
        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        return 0;
      });
    } else {
      r = [...r].sort((a, b) => {
        if (sort === "value") return Number(b.Totals?.total || 0) - Number(a.Totals?.total || 0);
        if (sort === "created") return new Date(createdAt(b) || 0) - new Date(createdAt(a) || 0);
        return new Date(updatedAt(b) || 0) - new Date(updatedAt(a) || 0);
      });
    }
    return r;
  }, [raw, scope, user, dealStage, tier, docStatus, buildType, urgencyB, supplierF, deliveryF, canMargin, marginF, expiryF, targetF, focus, flags, am, search, sort, colSort, managers, typeF, dateF]);

  // Option lists with live counts, derived from the base (non-archived) set.
  const base = useMemo(() => raw.filter((q) => String(q.Status).toLowerCase() !== "archived"), [raw]);
  const countBy = (fn) => {
    const m = {};
    base.forEach((q) => { const k = fn(q); if (k == null) return; (Array.isArray(k) ? k : [k]).forEach((kk) => { m[kk] = (m[kk] || 0) + 1; }); });
    return m;
  };
  const withCount = (opts, counts) => opts.map((o) => ({ ...o, label: `${o.label} (${counts[o.value] || 0})` }));
  const stageOpts = useMemo(() => dealStageGroups.map((g) => ({ value: g.key, label: `${g.label} (${g.count})`, colour: undefined })), [dealStageGroups]);
  const tierOpts = useMemo(() => tierGroups.map((g) => ({ value: g.label, label: `${g.label} (${g.count})` })), [tierGroups]);
  const statusOpts = useMemo(() => { const c = {}; STATUS_VALUES.forEach((s) => { c[s] = base.filter((q) => statusMatch(q, s)).length; }); return STATUS_VALUES.map((s) => ({ value: s, label: `${s} (${c[s]})` })); }, [base]);
  const buildOpts = useMemo(() => { const c = countBy((q) => q.BuildType || "—"); return Object.keys(c).sort().map((v) => ({ value: v, label: `${v} (${c[v]})` })); }, [base]);
  const urgencyOpts = useMemo(() => { const c = countBy(urgencyBucketOf); return withCount(URGENCY_OPTS, c); }, [base]);
  const supplierOpts = useMemo(() => { const c = countBy((q) => q.Supplier?.name || "—"); return Object.keys(c).sort().map((v) => ({ value: v, label: `${v} (${c[v]})` })); }, [base]);
  const deliveryOpts = useMemo(() => { const c = countBy((q) => q.Delivery?.method || q.Delivery || "—"); return Object.keys(c).sort().map((v) => ({ value: v, label: `${v} (${c[v]})` })); }, [base]);
  const marginOpts = useMemo(() => { const c = countBy(marginBandOf); return withCount(MARGIN_OPTS, c); }, [base]);
  const expiryOpts = useMemo(() => { const c = countBy(expiryBucketOf); return withCount(EXPIRY_OPTS, c); }, [base]);
  const targetOpts = useMemo(() => { const c = countBy(targetBucketOf); return withCount(TARGET_OPTS, c); }, [base]);
  const flagOpts = useMemo(() => { const c = {}; FLAG_OPTS.forEach((o) => { c[o.value] = base.filter((q) => flagMatch(q, o.value)).length; }); return withCount(FLAG_OPTS, c); }, [base]);
  const amOpts = useMemo(() => managers.map((m) => ({ value: m.email, label: m.name })), [managers]);

  // Customer-type dropdown options with live counts (client-side off the loaded set).
  const typeOpts = useMemo(() => {
    const c = countBy((q) => q._pipeline_type || "no_deal");
    return [
      { value: "lead", label: `Lead (${c.lead || 0})` },
      { value: "deal", label: `Customer (${c.deal || 0})` },
      { value: "no_deal", label: `No deal (${c.no_deal || 0})` },
    ];
  }, [base]);

  const focusOpts = useMemo(() => FOCUS_OPTS.map((o) => ({ ...o, label: `${o.label} (${(facets.focus || {})[o.value] || 0})` })), [facets]);

  const activeCount = dealStage.length + tier.length + docStatus.length + buildType.length + urgencyB.length + supplierF.length + deliveryF.length + (canMargin ? marginF.length : 0) + expiryF.length + targetF.length + (focus ? 1 : 0) + flags.length + am.length + (typeF ? 1 : 0) + (isDateFilterActive(dateF) ? 1 : 0);
  const clearAll = () => { setDealStage([]); setTier([]); setDocStatus([]); setBuildType([]); setUrgencyB([]); setSupplierF([]); setDeliveryF([]); setMarginF([]); setExpiryF([]); setTargetF([]); setFocus(""); setFlags([]); setAm([]); setTypeF(""); setDateF(EMPTY_DATE_FILTER); };

  // Saved Views — one-click filter presets (localStorage), parity with Deal/Lead pipelines.
  const viewSnapshot = useMemo(() => ({
    search, typeF, dealStage, tier, docStatus, buildType, urgencyB, supplierF,
    deliveryF, marginF, expiryF, targetF, focus, flags, am, scope, sort, dateF,
  }), [search, typeF, dealStage, tier, docStatus, buildType, urgencyB, supplierF, deliveryF, marginF, expiryF, targetF, focus, flags, am, scope, sort, dateF]);
  const savedViews = useSavedViews("quotes.savedViews", viewSnapshot);
  const activityIds = useMemo(() => rows.map((q) => q._customer_summary?.person_id).filter(Boolean), [rows]);
  const { summaries: activitySummaries, refresh: refreshActivity } = useActivitySummaries(activityIds);
  const applySavedView = useCallback((snap) => {
    setSearch(snap.search || ""); setTypeF(snap.typeF || ""); setDealStage(snap.dealStage || []);
    setTier(snap.tier || []); setDocStatus(snap.docStatus || []); setBuildType(snap.buildType || []);
    setUrgencyB(snap.urgencyB || []); setSupplierF(snap.supplierF || []); setDeliveryF(snap.deliveryF || []);
    setMarginF(snap.marginF || []); setExpiryF(snap.expiryF || []); setTargetF(snap.targetF || []);
    setFocus(snap.focus || ""); setFlags(snap.flags || []); setAm(snap.am || []);
    setScope(snap.scope || "all"); setSort(snap.sort || "updated"); setDateF(normalizeDateFilter(snap.dateF));
  }, []);

  const toggle = (arr, set, v) => set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  const toggleRow = (id) => setExpanded((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const [person360Id, setPerson360Id] = useState(null);
  const open360 = async (q) => {
    const personId = q.Customer?.personId || q.Customer?.PersonId;
    const email = q.Customer?.email;
    if (!personId && !email) { toast.error("No customer is attached to this quote"); return; }
    const id = await resolveCrmPersonId({ personId, email });
    if (id) setPerson360Id(id);
    else toast.error("No customer record is linked to this quote yet");
  };

  const Chip = ({ on, onClick, children, testid, tone = "" }) => (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${on ? `bg-white/15 text-white border-white/30 ${tone}` : "border-white/10 text-slate-400 hover:text-white"}`}>
      {children}
    </button>
  );

  return (
    <Layout mainProps={theme === "light" ? { "data-talmia-theme": "light" } : {}}>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-orange-400 font-mono mb-1">Quote Builder ▸ View Quotes</div>
          <h1 className="font-display text-3xl font-bold tracking-tight" data-testid="view-quotes-title">Quotes</h1>
          <p className="text-slate-400 text-sm mt-1" data-testid="view-quotes-summary">
            {raw.length} quotes · <span className="text-emerald-300">{money(openValue)}</span> open value — one row per quote · most recent first
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="px-3 py-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-center" data-testid="view-quotes-paid-tile" title="Quotes marked Paid this calendar month">
            <div className="text-lg font-bold text-emerald-300 tabular-nums leading-none">{paidThisMonth}</div>
            <div className="text-[9px] uppercase tracking-widest text-emerald-400/70 font-mono mt-1">Paid this month</div>
          </div>
          <button type="button" onClick={() => setDupesOpen(true)} data-testid="vq-dupes-open" title="Find likely duplicate quotes, compare and archive"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-amber-500/30 text-sm text-amber-200 hover:bg-amber-500/10"><Copy className="w-4 h-4" /> Duplicates</button>
          <button type="button" onClick={() => setBinOpen(true)} data-testid="vq-bin-open" title="Archived & deleted quotes — restore any of them"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/10 text-sm text-slate-300 hover:text-white hover:bg-white/5"><ArchiveRestore className="w-4 h-4" /> Archived &amp; deleted</button>
          <button type="button" onClick={load} disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm border border-white/10 text-slate-300 hover:bg-white/5 disabled:opacity-50" data-testid="view-quotes-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          {can("edit") && <Link to="/quotes/new" className="btn-coral inline-flex items-center gap-2" data-testid="view-quotes-new">
            <Plus className="w-4 h-4" /> New quote
          </Link>}
        </div>
      </div>

      {/* No-customer banner */}
      {noCust.length > 0 && (
        <div className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/[0.06] px-4 py-3" data-testid="view-quotes-nocust-banner">
          <div className="flex items-center gap-2 text-amber-200 text-sm">
            <AlertTriangle className="w-4 h-4" />
            <span><b>{noCust.length}</b> quote{noCust.length === 1 ? "" : "s"} with no customer attached — they live here and nowhere else in the pipeline.</span>
            <button onClick={() => { setFlags((f) => [...new Set([...f, "no_customer"])]); }} className="ml-auto text-[11px] font-mono text-amber-300 hover:text-amber-100 underline underline-offset-2" data-testid="view-quotes-manage-nocust">Manage all →</button>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {noCust.slice(0, 8).map((q) => (
              <Link key={q.id} to={`/quotes/${q.id}/edit`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-black/30 border border-amber-500/20 text-[11px] font-mono text-amber-200 hover:bg-black/50">
                <FileText className="w-3 h-3" /> {q.ref} · {q.status}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="view-quotes-search"
            placeholder="Search ref, project, postcode, name, company, email, phone, supplier, AM…"
            className="w-full pl-9 pr-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-sm text-white focus:border-orange-400/60 outline-none" />
        </div>
        <div className="inline-flex items-stretch gap-0.5 rounded-lg p-0.5 border border-white/10 bg-white/[0.02]" data-testid="vq-scope">
          {[["all", "All quotes"], ["mine", "Assigned to me"], ["created", "Created by me"], ["collab", "Collaborating"]].map(([k, lbl]) => (
            <button key={k} type="button" onClick={() => setScope(k)} data-testid={`vq-scope-${k}`}
              className={`px-2.5 h-8 rounded-md text-[11.5px] font-medium transition-colors ${scope === k ? "bg-orange-500/15 text-orange-200" : "text-slate-400 hover:text-white hover:bg-white/5"}`}>
              {lbl}
            </button>
          ))}
        </div>
        <select value={sort} onChange={(e) => { setSort(e.target.value); setColSort(null); }} data-testid="view-quotes-sort"
          className="h-9 px-3 rounded-lg bg-slate-900 border border-white/10 text-[12.5px] text-white outline-none [color-scheme:dark]">
          <option value="created">Sort: Created (newest)</option>
          <option value="updated">Sort: Last updated</option>
          <option value="value">Sort: Value</option>
        </select>
        <span className="text-[11px] text-slate-500 font-mono">{rows.length} of {raw.length}</span>
      </div>

      {/* Filter bar — searchable multi-select dropdowns (Task Module 05c) */}
      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3 mb-4 space-y-2.5" data-testid="view-quotes-filter-bar">
        <div className="flex items-center gap-2 flex-wrap" data-testid="vq-filter-core">
          <span className="w-16 shrink-0 text-[9.5px] uppercase tracking-widest text-slate-500 font-mono">Filter</span>
          {/* Core */}
          <select value={typeF} onChange={(e) => setTypeF(e.target.value)} data-testid="vq-type"
            title="Customer type — lead (prospect) vs existing customer vs no linked deal"
            className="h-9 px-2 rounded-md bg-slate-900/70 border border-white/10 text-[12px] text-white outline-none focus:border-orange-400/60 min-w-[130px]">
            <option value="">Type: any</option>
            {typeOpts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <MultiSelectDropdown label="Stage"    testid="vq-stage"    options={stageOpts}    selected={dealStage} onChange={setDealStage} width="min-w-[130px]" />
          <MultiSelectDropdown label="Tier"     testid="vq-tier"     options={tierOpts}     selected={tier}      onChange={setTier} width="min-w-[110px]" />
          <MultiSelectDropdown label="Status"   testid="vq-status"   options={statusOpts}   selected={docStatus} onChange={setDocStatus} width="min-w-[120px]" />
          <MultiSelectDropdown label="AM"       testid="vq-am"       options={amOpts}       selected={am}        onChange={setAm} width="min-w-[120px]" />
          <DateRangeFilter value={dateF} onChange={setDateF} fields={QUOTE_DATE_FIELDS} testid="vq-date-filter" />
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-testid="vq-filter-more">
          <span className="w-16 shrink-0 text-[9.5px] uppercase tracking-widest text-slate-500 font-mono">Refine</span>
          {/* Quote signals */}
          <MultiSelectDropdown label="Build"    testid="vq-build"    options={buildOpts}    selected={buildType} onChange={setBuildType} width="min-w-[120px]" />
          <MultiSelectDropdown label="Urgency"  testid="vq-urgency"  options={urgencyOpts}  selected={urgencyB}  onChange={setUrgencyB} width="min-w-[120px]" />
          <select value={focus} onChange={(e) => setFocus(e.target.value)} data-testid="vq-focus"
            title="Timeline & chase buckets — same definitions as Deal Pipeline"
            className="h-9 px-2 rounded-md bg-slate-900/70 border border-white/10 text-[12px] text-white outline-none focus:border-orange-400/60 min-w-[150px]">
            <option value="">Focus: any</option>
            {focusOpts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>

          <span className="w-px h-6 bg-white/10 mx-0.5" />
          {/* Commercial */}
          <MultiSelectDropdown label="Supplier" testid="vq-supplier" options={supplierOpts} selected={supplierF} onChange={setSupplierF} width="min-w-[130px]" />
          <MultiSelectDropdown label="Delivery" testid="vq-delivery" options={deliveryOpts} selected={deliveryF} onChange={setDeliveryF} width="min-w-[120px]" />
          {canMargin && <MultiSelectDropdown label="Margin" testid="vq-margin" options={marginOpts} selected={marginF} onChange={setMarginF} width="min-w-[120px]" />}
          <MultiSelectDropdown label="Expiry"   testid="vq-expiry"   options={expiryOpts}   selected={expiryF}   onChange={setExpiryF} width="min-w-[120px]" />
          <MultiSelectDropdown label="Target"   testid="vq-target"   options={targetOpts}   selected={targetF}   onChange={setTargetF} width="min-w-[120px]" />
          <MultiSelectDropdown label="Flags"    testid="vq-flags"    options={flagOpts}     selected={flags}     onChange={setFlags} width="min-w-[120px]" />

        </div>

        {activeCount > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap pt-1 border-t border-white/[0.06]" data-testid="vq-active-summary">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{activeCount} active</span>
            <button onClick={clearAll} className="text-[11px] font-mono text-orange-300 hover:text-orange-100 underline underline-offset-2" data-testid="vq-clear-all">Clear all</button>
            <span className="text-[11px] text-slate-500 font-mono ml-auto">{rows.length} of {base.length}</span>
          </div>
        )}

        {/* Saved views — one-click filter presets (localStorage), parity with Deal/Lead pipelines. */}
        <div className="pt-1 border-t border-white/[0.04]">
          <SavedViewChips
            views={savedViews.views}
            currentSnapshot={viewSnapshot}
            onApply={applySavedView}
            onDelete={savedViews.remove}
            onSave={savedViews.saveCurrent}
            testidPrefix="quote-view"
          />
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-white/[0.06] overflow-x-auto" data-testid="view-quotes-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[9.5px] uppercase tracking-widest text-slate-500 font-mono border-b border-white/10">
              <th className="w-8"></th>
              <SortTh k="ref" label="Quote · Project" align="left" colSort={colSort} onColSort={onColSort} />
              <SortTh k="customer" label="Customer" align="left" colSort={colSort} onColSort={onColSort} />
              <SortTh k="type" label="Type" colSort={colSort} onColSort={onColSort} />
              <SortTh k="tier" label="Tier" colSort={colSort} onColSort={onColSort} />
              <SortTh k="status" label="Document" colSort={colSort} onColSort={onColSort} />
              <SortTh k="dealstage" label="Deal Stage" colSort={colSort} onColSort={onColSort} />
              <SortTh k="value" label="Value" align="right" colSort={colSort} onColSort={onColSort} />
              {canMargin && <SortTh k="margin" label="Margin" align="right" colSort={colSort} onColSort={onColSort} />}
              <SortTh k="complete" label="Complete" colSort={colSort} onColSort={onColSort} />
              <th className="text-left px-2 py-2">Urgency</th>
              <SortTh k="expires" label="Expires" colSort={colSort} onColSort={onColSort} />
              <SortTh k="am" label="AM" colSort={colSort} onColSort={onColSort} />
              <th className="text-left px-2 py-2">Collaborators</th>
              <th className="text-left px-2 py-2" data-testid="view-quotes-col-activity">Activity</th>
              <SortTh k="created" label="Created" colSort={colSort} onColSort={onColSort} />
              <SortTh k="updated" label="Updated" colSort={colSort} onColSort={onColSort} />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={canMargin ? 17 : 16} className="text-center py-10 text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={canMargin ? 17 : 16} className="text-center py-10 text-slate-500 text-sm">No quotes match these filters.</td></tr>
            )}
            {!loading && rows.map((q) => {
              const id = q._id;
              const li = q.LineItems || [];
              const allNotes = q.StaffNotes || [];
              const notes = allNotes.filter((n) => !isSnapshot(n));
              const sysNotes = allNotes.filter(isSnapshot);
              const atts = q.Attachments || [];
              const exp = expiry(q.ExpiryDate, q.Status);
              const isOpen = expanded.has(id);
              const mgn = quoteMargin(q);
              const margin = mgn.marginPct;
              return (
                <Fragment key={id}>
                  <tr className="border-b border-white/[0.04] hover:bg-white/[0.02] align-top" data-testid={`view-quotes-row-${q.QuoteRef}`}>
                    <td className="pl-2 py-2 align-middle">
                      <button onClick={() => toggleRow(id)} className="flex items-center justify-center w-8 h-12 rounded-md text-slate-400 hover:text-white hover:bg-white/5 transition-colors" data-testid={`view-quotes-expand-${q.QuoteRef}`}>
                        {isOpen ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                      </button>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5 flex-wrap [&>*]:whitespace-nowrap">
                        <Link to={`/quotes/${id}/edit`} className="font-mono font-semibold text-sky-300 hover:text-sky-200 hover:underline underline-offset-2 whitespace-nowrap" data-testid={`view-quotes-ref-${q.QuoteRef}`}>{q.QuoteRef}</Link>
                        <Link to={`/quotes/${id}/edit`} title="Open in Quote Builder" data-testid={`view-quotes-edit-${q.QuoteRef}`}
                          className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border border-orange-500/40 text-orange-300 hover:bg-orange-500/15 transition-colors">
                          <SquarePen className="w-3 h-3" /> Edit
                        </Link>
                        {alerts[id] && (
                          <InfoGlyph icon={AlertTriangle} count={alerts[id].count} tone="text-amber-400 notif-flash" testid={`view-quotes-info-alert-${q.QuoteRef}`}>
                            <div className="text-[10px] uppercase tracking-widest text-amber-300 font-mono mb-1">
                              {alerts[id].count} unread update{alerts[id].count > 1 ? "s" : ""}{alerts[id].actors?.length ? ` · ${alerts[id].actors.join(", ")}` : ""}
                            </div>
                            {(alerts[id].items || []).map((it, i) => (
                              <div key={i} className="mb-1 last:mb-0">
                                <div className="text-[11px] text-white leading-snug">{it.title}</div>
                                {it.body && <div className="text-[10.5px] text-slate-400 leading-snug">{it.body}</div>}
                              </div>
                            ))}
                          </InfoGlyph>
                        )}
                        {q._dup_count > 0 && <span className="text-[9px] font-mono px-1 py-0.5 rounded bg-fuchsia-500/20 text-fuchsia-300 border border-fuchsia-500/30" data-testid={`view-quotes-dup-${q.QuoteRef}`}>dup ×{q._dup_count + 1}</span>}
                        {q._no_customer && <span className="text-[9px] font-mono px-1 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30" data-testid={`view-quotes-nocust-${q.QuoteRef}`}>⚠ no customer</span>}
                        {(q._focus || []).filter((f) => FOCUS_BADGE[f]).map((f) => (
                          <span key={f} className={`text-[9px] font-mono uppercase px-1 py-0.5 rounded border ${FOCUS_BADGE[f].cls}`} data-testid={`view-quotes-focus-${f}-${q.QuoteRef}`}>
                            {FOCUS_BADGE[f].label}
                          </span>
                        ))}
                        {q.sla_breached && (
                          <span className="text-[9px] font-mono uppercase px-1 py-0.5 rounded border bg-amber-500/20 text-amber-300 border-amber-500/40"
                            title={`No chase for 48h+ since sent — ${q.sla_hours_over}h over`} data-testid={`view-quotes-sla-${q.QuoteRef}`}>
                            SLA +{q.sla_hours_over}h
                          </span>
                        )}
                      </div>
                      {q.ProjectRef && <div className="text-[11.5px] text-amber-200/90 font-medium truncate max-w-[240px] mt-0.5" title={q.ProjectRef} data-testid={`view-quotes-project-${q.QuoteRef}`}>{q.ProjectRef}</div>}
                      {sitePostcode(q) && (
                        <div className="text-[10.5px] text-slate-500 font-mono mt-0.5 inline-flex items-center gap-1" data-testid={`view-quotes-postcode-${q.QuoteRef}`}>
                          <MapPin className="w-2.5 h-2.5" />{sitePostcode(q)}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2" data-testid={`view-quotes-customercol-${q.QuoteRef}`}>
                      {q._no_customer && !custName(q) ? (
                        <div className="text-[12px] text-slate-500 italic">— no customer</div>
                      ) : (
                        <button onClick={() => open360(q)} className="text-[12px] font-medium text-teal-300 hover:text-teal-200 text-left" data-testid={`view-quotes-cust-${q.QuoteRef}`}>
                          {custName(q) || q.Customer?.company || q._customer_summary?.company || "—"}
                        </button>
                      )}
                      {(q.Customer?.company || q._customer_summary?.company) && custName(q) && (
                        <div className="text-[11px] text-slate-500">{q.Customer?.company || q._customer_summary?.company}</div>
                      )}
                      {(q.BuildType || q.Timeline?.targetDate || q.Timeline?.asap) && (
                        <div className="text-[10.5px] text-slate-500 font-mono mt-0.5">
                          {[q.BuildType, q.Timeline?.targetDate ? fmtDate(q.Timeline.targetDate) : null, q.Timeline?.asap ? "ASAP" : null].filter(Boolean).join(" · ")}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2" data-testid={`view-quotes-type-${q.QuoteRef}`}>
                      {(() => {
                        const tm = typeMeta(q._pipeline_type);
                        return tm
                          ? <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border ${tm.cls}`}>{tm.label}</span>
                          : <span className="text-slate-600 text-[11px]" title="No linked customer/deal">—</span>;
                      })()}
                    </td>
                    <td className="px-2 py-2">
                      {custTier(q)
                        ? <span title={custTier(q)} className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border ${tierClass(custTier(q))}`}>{tierShort(custTier(q))}</span>
                        : <span className="text-slate-600">—</span>}
                    </td>
                    <td className="px-2 py-2" data-testid={`view-quotes-status-${q.QuoteRef}`} onClick={(e) => e.stopPropagation()}>
                      <QuoteStatusMenu value={docState(q.Status, { payment: q.Payment, sent: q.Sent })} onChange={(next) => changeStatus(q, next)} />
                    </td>
                    <td className="px-2 py-2" data-testid={`view-quotes-dealstage-${q.QuoteRef}`}>
                      {q._deal_stage
                        ? (() => {
                            const tm = typeMeta(q._deal_stage.pipeline);
                            return (
                              <button onClick={() => open360(q)} title={`${q._deal_stage.pipeline === "lead" ? "Lead" : "Deal"} stage: ${q._deal_stage.label} — click to open Customer 360`}
                                style={tm ? { borderLeft: `3px solid ${tm.edge}` } : undefined}
                                className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border transition-colors hover:brightness-125 ${stageClass(q._deal_stage.color)}`}>
                                {tm && <span className="opacity-70 mr-1">{tm.prefix}·</span>}{q._deal_stage.label}
                              </button>
                            );
                          })()
                        : <span className="text-slate-600 text-[11px]" title="This quote isn't linked to a customer, so it sits in no deal pipeline">— no deal</span>}
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums text-white" title={`ex-VAT ${money(q.Totals?.sub)} · VAT ${money(q.Totals?.vat)}`}>{money(q.Totals?.total)}</td>
                    {canMargin && (
                      <td className={`px-2 py-2 text-right tabular-nums ${margin == null ? "text-slate-600" : margin < 0 ? "text-rose-400" : margin < 10 ? "text-amber-300" : "text-emerald-300"}`}>
                        {margin == null ? "—" : `${Number(margin).toFixed(1)}%`}
                      </td>
                    )}
                    <td className="px-2 py-2" data-testid={`view-quotes-complete-${q.QuoteRef}`}>
                      {(() => {
                        const c = quoteCompletion(q);
                        const tone = { ok: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10", teal: "text-teal-300 border-teal-500/40 bg-teal-500/10", brass: "text-amber-300 border-amber-500/40 bg-amber-500/10", red: "text-rose-300 border-rose-500/40 bg-rose-500/10" }[completionTone(c.pct)];
                        return (
                          <span title={c.missing.length ? `Missing: ${c.missing.join(", ")}` : "Everything filled in"} className={`inline-flex items-center gap-1.5 text-[11px] font-mono px-1.5 py-0.5 rounded border tabular-nums ${tone}`}>
                            <span className="w-8 h-1 rounded-full bg-white/10 overflow-hidden"><span className="block h-full bg-current" style={{ width: `${c.pct}%` }} /></span>{c.pct}%
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-2 py-2" data-testid={`view-quotes-urgency-${q.QuoteRef}`}>
                      {(() => {
                        const b = urgencyBand(q.Timeline?.desperation, isPaidQ(q));
                        return (
                          <span className="inline-flex items-center gap-1">
                            <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border ${b.cls} ${b.anim}`}
                              title={`Urgency ${q.Timeline?.desperation ?? 0}/10`} data-testid={`view-quotes-urgency-chip-${q.QuoteRef}`}>
                              {b.label}
                            </span>
                            {q.Timeline?.asap && (
                              <span className="text-[9px] font-mono px-1 py-0.5 rounded bg-rose-500/20 text-rose-300 border border-rose-500/40 urg-critical">ASAP</span>
                            )}
                          </span>
                        );
                      })()}
                    </td>
                    <td className={`px-2 py-2 text-[12px] font-mono ${exp.cls}`}>{exp.label}</td>
                    <td className="px-2 py-2">
                      {q.AccountManager
                        ? <OwnerChip primary={q.AccountManager} />
                        : <span className="text-slate-600">—</span>}
                    </td>
                    <td className="px-2 py-2" data-testid={`view-quotes-collabs-${q.QuoteRef}`}>
                      {(() => {
                        const cols = collabNames(q);
                        if (cols.length === 0) return <span className="text-slate-600 text-[11px]">—</span>;
                        const shown = cols.slice(0, 3);
                        const extra = cols.length - shown.length;
                        return (
                          <div className="inline-flex items-center" title={cols.join(", ")}>
                            {shown.map((name, i) => (
                              <div key={name} style={{ marginLeft: i === 0 ? 0 : -8 }}>
                                <InitialBadge name={name} size={24} testid={`view-quotes-collab-avatar-${q.QuoteRef}-${i}`} />
                              </div>
                            ))}
                            {extra > 0 && (
                              <span className="ml-1 text-[10px] font-mono text-slate-400 px-1.5 py-0.5 rounded-full bg-white/5 border border-white/10" data-testid={`view-quotes-collab-more-${q.QuoteRef}`}>+{extra}</span>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-2 py-2" data-testid={`view-quotes-activity-${q.QuoteRef}`}>
                      {q._customer_summary?.person_id ? (
                        <ActivityCell
                          personId={q._customer_summary.person_id}
                          am={q.AccountManager}
                          summary={activitySummaries[q._customer_summary.person_id]}
                          onSaved={refreshActivity}
                          testIdSuffix={q.QuoteRef}
                        />
                      ) : (
                        <span className="text-slate-600 text-[11px]">—</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-[11px] font-mono text-slate-300 whitespace-nowrap" data-testid={`view-quotes-created-${q.QuoteRef}`} title={createdAt(q)}>{fmtDate(createdAt(q))}</td>
                    <td className="px-2 py-2 text-[11px] font-mono text-slate-400" title={updatedAt(q)}>{relTime(updatedAt(q))}</td>
                  </tr>
                  {isOpen && (
                    <tr className="border-b border-white/[0.06] bg-white/[0.015]" data-testid={`view-quotes-expand-row-${q.QuoteRef}`}>
                      <td></td>
                      <td colSpan={canMargin ? 16 : 15} className="px-3 py-3">
                        {/* Duplicates: clickable banner → the customer's other quotes, tick + archive */}
                        {q._dup_count > 0 && (
                          <div className="mb-3">
                            <button onClick={() => toggleDup(q)} data-testid={`view-quotes-dup-banner-${q.QuoteRef}`}
                              className="flex items-center gap-2 w-full text-[12px] text-fuchsia-300 hover:text-fuchsia-200">
                              {dupOpen[id] ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                              ◈ {q._dup_count + 1} quotes for this customer — possible duplicate
                              {!q._no_customer && <span onClick={(e) => { e.stopPropagation(); open360(q); }} className="ml-auto text-slate-300 hover:text-white inline-flex items-center gap-1 cursor-pointer">Open Customer 360 <ExternalLink className="w-3 h-3" /></span>}
                            </button>
                            {dupOpen[id] && (
                              <div className="mt-2 rounded-lg border border-fuchsia-500/20 bg-fuchsia-500/[0.04] p-2.5" data-testid={`view-quotes-dup-list-${q.QuoteRef}`}>
                                {dupData[id] == null && <div className="text-[11px] text-slate-500 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>}
                                {dupData[id] && dupData[id].length === 0 && <div className="text-[11px] text-slate-500">No other quotes found.</div>}
                                {dupData[id] && dupData[id].length > 0 && (
                                  <>
                                    <div className="flex items-center justify-between mb-1.5">
                                      <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono">Other quotes for this customer</div>
                                      <button onClick={() => archiveSelected(q)} disabled={!(dupSel[id]?.size) || dupBusy[id]}
                                        data-testid={`view-quotes-dup-archive-${q.QuoteRef}`}
                                        className={`text-[11px] font-semibold px-2.5 py-1 rounded border inline-flex items-center gap-1 ${(dupSel[id]?.size && !dupBusy[id]) ? "border-rose-500/50 bg-rose-500/15 text-rose-200 hover:bg-rose-500/25" : "border-white/10 text-slate-500 cursor-not-allowed"}`}>
                                        {dupBusy[id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Archive className="w-3 h-3" />} Archive selected{dupSel[id]?.size ? ` (${dupSel[id].size})` : ""}
                                      </button>
                                    </div>
                                    {dupData[id].map((s) => (
                                      <label key={s.id} className="flex items-center gap-2 py-1 text-[11.5px] text-slate-200 hover:bg-white/[0.03] rounded px-1 cursor-pointer" data-testid={`view-quotes-dup-item-${s.ref}`}>
                                        <input type="checkbox" checked={!!dupSel[id]?.has(s.id)} onChange={() => toggleDupSel(id, s.id)} className="accent-fuchsia-500" data-testid={`view-quotes-dup-check-${s.ref}`} />
                                        <Link to={`/quotes/${s.id}/edit`} className="font-mono text-sky-300 hover:underline">{s.ref}</Link>
                                        <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border ${docClass(s.status)}`}>{docState(s.status)}</span>
                                        <span className="tabular-nums text-slate-300 ml-auto">{money(s.value)}</span>
                                        <span className="text-slate-500 w-14 text-right">{s.lines} line{s.lines === 1 ? "" : "s"}</span>
                                        <span className="text-slate-500 font-mono w-12 text-right">{relTime(s.updated)}</span>
                                      </label>
                                    ))}
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[repeat(13,minmax(0,1fr))] gap-3 max-w-[calc(100vw-2rem)] md:max-w-none sticky left-0">
                          {/* ① CUSTOMER — USP snapshot */}
                          <div className="lg:col-span-3 rounded-lg border border-white/[0.06] p-2.5" data-testid={`view-quotes-customer-${q.QuoteRef}`}>
                            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-1.5">Customer</div>
                            {(q._no_customer || !q._customer_summary) ? (
                              <div className="text-[11px] text-slate-500 italic">— no customer attached</div>
                            ) : (() => {
                              const cs = q._customer_summary;
                              const tm = typeMeta(q._pipeline_type);
                              return (
                                <div className="space-y-1.5">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <button onClick={() => open360(q)} className="text-[12.5px] font-semibold text-teal-300 hover:text-teal-200 text-left" data-testid={`view-quotes-cust-name-${q.QuoteRef}`}>{cs.name || q.Customer?.name || "—"}</button>
                                    {cs.tier && <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded border ${tierClass(cs.tier)}`}>{tierShort(cs.tier)}</span>}
                                    {tm && <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded border ${tm.cls}`}>{tm.label}</span>}
                                  </div>
                                  {cs.company && <div className="text-[11px] text-slate-400">{cs.company}</div>}
                                  <div className="grid grid-cols-2 gap-x-2 gap-y-1 pt-1 text-[11px]">
                                    <div className="text-slate-500">Lifetime spend</div><div className="text-right tabular-nums text-emerald-300">{money(cs.ltv)}</div>
                                    <div className="text-slate-500">Orders</div><div className="text-right tabular-nums text-slate-200">{cs.orders_count ?? 0}</div>
                                    <div className="text-slate-500">Avg order</div><div className="text-right tabular-nums text-slate-200">{money(cs.aov)}</div>
                                    <div className="text-slate-500">Last order</div><div className="text-right text-slate-200">{fmtDate(cs.last_order_date)}</div>
                                  </div>
                                  <div className="pt-1 border-t border-white/[0.06] space-y-0.5 text-[11px]">
                                    {cs.email && <div className="truncate text-slate-300" title={cs.email}>✉ {cs.email}</div>}
                                    {cs.phone && <div className="text-slate-300">☎ {cs.phone}</div>}
                                    {(cs.post_code || cs.city) && <div className="text-slate-500">{[cs.city, cs.post_code].filter(Boolean).join(" · ")}</div>}
                                    <div className="text-slate-500">AM: <span className="text-slate-300">{q.AccountManager || "—"}</span></div>
                                  </div>
                                  <button onClick={() => open360(q)} className="mt-1.5 w-full text-[11px] font-semibold px-2.5 py-1.5 rounded border border-teal-400/50 bg-teal-500/10 text-teal-200 hover:bg-teal-500/20 inline-flex items-center justify-center gap-1" data-testid={`view-quotes-cust-open360-${q.QuoteRef}`}><ExternalLink className="w-3 h-3" /> Open Customer 360</button>
                                </div>
                              );
                            })()}
                            <div className="mt-2.5 pt-2.5 border-t border-white/[0.06]" data-testid={`view-quotes-delivery-${q.QuoteRef}`}>
                              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-1">Supply &amp; delivery</div>
                              <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-[11px] mb-1">
                                <div className="text-slate-500">Supplier</div><div className="text-right text-slate-200 truncate">{q.Supplier?.name || "—"}</div>
                                <div className="text-slate-500">Delivery</div><div className="text-right text-slate-200 truncate">{q.Delivery?.method || (typeof q.Delivery === "string" ? q.Delivery : "") || "—"}</div>
                              </div>
                              {deliveryChecklist(q).map((c, i) => (
                                <div key={i} className="flex items-start gap-1.5 text-[11px] text-slate-300 py-0.5">
                                  <CheckIcon className="w-3 h-3 text-emerald-400 shrink-0 mt-0.5" /><span>{c}</span>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* ② WORKFLOW · PROJECT · ACTIONS */}
                          <div className="lg:col-span-3 rounded-lg border border-white/[0.06] p-2.5 space-y-2.5" data-testid={`view-quotes-wiring-${q.QuoteRef}`}>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <div className="text-[9.5px] uppercase tracking-widest text-slate-500 font-mono mb-1.5">Document status</div>
                                <QuoteStatusMenu value={docState(q.Status, { payment: q.Payment, sent: q.Sent })} onChange={(next) => changeStatus(q, next)} />
                              </div>
                              <div>
                                <div className="text-[9.5px] uppercase tracking-widest text-slate-500 font-mono mb-1.5">{q._deal_stage?.pipeline === "lead" ? "Lead stage" : "Deal stage"}</div>
                                {!q._deal_stage || !q._customer_summary?.person_id ? (
                                  <div className="text-[11px] text-slate-500 py-1.5">— no deal</div>
                                ) : q._deal_stage.pipeline === "lead" ? (
                                  <div className="text-[11px] text-slate-300 py-1" data-testid={`view-quotes-dealstage-readonly-${q.QuoteRef}`}>
                                    {q._deal_stage.label}
                                    <span className="block text-[9.5px] text-slate-500 mt-0.5">Move on the Lead Pipeline board.</span>
                                  </div>
                                ) : (
                                  <select value={q._deal_stage.key} onChange={(e) => changeDealStage(q, e.target.value)}
                                    className="h-[38px] text-[12px] bg-black/40 border border-white/15 rounded-lg px-2 text-slate-100 w-full [color-scheme:dark]" data-testid={`view-quotes-dealstage-select-${q.QuoteRef}`}>
                                    {dealStageOpts.map((s) => (
                                      <option key={s.key} value={s.key}>{s.label}</option>
                                    ))}
                                  </select>
                                )}
                              </div>
                            </div>
                            <div className="pt-2.5 border-t border-white/[0.06]" data-testid={`view-quotes-project-panel-${q.QuoteRef}`}>
                              <div className="text-[9.5px] uppercase tracking-widest text-slate-500 font-mono mb-1">Project</div>
                              <div className="text-[12.5px] font-medium text-amber-200/90 break-words">{q.ProjectRef || <span className="text-slate-500 font-normal">No project name</span>}</div>
                              <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 mt-1.5 text-[11px]">
                                <div className="text-slate-500">Site postcode</div><div className="text-right font-mono text-slate-200">{sitePostcode(q) || "—"}</div>
                                <div className="text-slate-500">Build type</div><div className="text-right text-slate-200">{q.BuildType || "—"}</div>
                                <div className="text-slate-500">Needed by</div><div className="text-right text-slate-200">{q.Timeline?.asap ? "ASAP" : (q.Timeline?.targetDate ? fmtDate(q.Timeline.targetDate) : "—")}</div>
                                <div className="text-slate-500">Expires</div><div className={`text-right font-mono ${exp.cls}`}>{exp.label}</div>
                              </div>
                            </div>
                            <div className="pt-2.5 border-t border-white/[0.06]">
                              <div className="text-[9.5px] uppercase tracking-widest text-slate-500 font-mono mb-1.5">Actions</div>
                              <div className="flex flex-wrap gap-1.5">
                                <Link to={`/quotes/${id}/pdf`} className="text-[11px] font-semibold px-2.5 py-1.5 rounded border border-white/20 text-slate-100 hover:bg-white/10 inline-flex items-center gap-1" data-testid={`view-quotes-pdf-${q.QuoteRef}`}><FileText className="w-3 h-3" /> PDF</Link>
                                {!q._no_customer && q._customer_summary && <button onClick={() => open360(q)} className="text-[11px] font-semibold px-2.5 py-1.5 rounded border border-teal-400/50 bg-teal-500/10 text-teal-200 hover:bg-teal-500/20 inline-flex items-center gap-1" data-testid={`view-quotes-open360-${q.QuoteRef}`}><ExternalLink className="w-3 h-3" /> Customer 360</button>}
                                <button onClick={() => extendQuote(q)} title="Reset the customer-facing validity date. Quotes are never locked — this just refreshes 'valid until'." className="text-[11px] font-semibold px-2.5 py-1.5 rounded border border-white/20 text-slate-100 hover:bg-white/10 inline-flex items-center gap-1" data-testid={`view-quotes-extend-${q.QuoteRef}`}><RefreshCw className="w-3 h-3" /> Extend</button>
                                {q.QuoteVariant === "proforma" && <ProformaMarkButton quote={q} onDone={() => load()} testid={`view-quotes-proforma-${q.QuoteRef}`} />}
                              </div>
                            </div>
                          </div>

                          {/* ③ LINE ITEMS & MONEY */}
                          <div className="lg:col-span-3 rounded-lg border border-white/[0.06] p-2.5" data-testid={`view-quotes-money-${q.QuoteRef}`}>
                            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-1.5">Line items &amp; money</div>
                            {li.length === 0 && <div className="text-[11px] text-slate-500">No line items</div>}
                            {li.length > 0 && (
                              <div className="text-[11px]">
                                <div className="flex gap-2 text-[9.5px] uppercase tracking-wide text-slate-500 font-mono pb-1 border-b border-white/[0.06]">
                                  <span className="flex-1">Item</span><span className="w-8 text-right">Qty</span><span className="w-16 text-right">Unit</span><span className="w-16 text-right">Total</span>{canMargin && <span className="w-12 text-right">Mgn</span>}
                                </div>
                                {li.map((l, i) => {
                                  const lm = lineMarginPct(l);
                                  return (
                                    <div key={i} className="flex gap-2 py-0.5 text-slate-200 border-b border-white/[0.03]">
                                      <span className="flex-1 truncate" title={l.name || l.sku}>{l.name || l.sku}</span>
                                      <span className="w-8 text-right tabular-nums text-slate-400">{l.qty}</span>
                                      <span className="w-16 text-right tabular-nums text-slate-400">{money(l.unitPrice)}</span>
                                      <span className="w-16 text-right tabular-nums">{money(lineTotal(l))}</span>
                                      {canMargin && <span className={`w-12 text-right tabular-nums ${lm == null ? "text-slate-600" : lm < 0 ? "text-rose-400" : lm < 10 ? "text-amber-300" : "text-emerald-300"}`}>{lm == null ? "—" : `${lm.toFixed(0)}%`}</span>}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            <div className="mt-2 pt-2 border-t border-white/10 space-y-0.5 text-[11.5px]">
                              <div className="flex justify-between text-slate-300"><span>Subtotal ex VAT</span><span className="tabular-nums">{money(mgn.revenue)}</span></div>
                              {q.Delivery?.cost > 0 && <div className="flex justify-between text-slate-300"><span>Delivery</span><span className="tabular-nums">{money(q.Delivery.cost)}</span></div>}
                              <div className="flex justify-between text-slate-300"><span>VAT</span><span className="tabular-nums">{money(q.Totals?.vat)}</span></div>
                              <div className="flex justify-between text-white font-semibold"><span>Total</span><span className="tabular-nums">{money(q.Totals?.total)}</span></div>
                              {canMargin && (
                                <div className="flex justify-between pt-1 mt-1 border-t border-white/[0.06]">
                                  <span className="text-slate-400">Margin (post-rebate)</span>
                                  <span className={`tabular-nums font-semibold ${margin == null ? "text-slate-500" : margin < 0 ? "text-rose-400" : margin < 10 ? "text-amber-300" : "text-emerald-300"}`}>{margin == null ? "—" : `${money(mgn.marginAbs)} · ${margin.toFixed(1)}%`}</span>
                                </div>
                              )}
                              <div className="flex justify-between text-[10.5px] text-slate-500 pt-0.5"><span>Supplier</span><span>{q.Supplier?.name || "—"}{mgn.rebatePct ? ` · rebate ${mgn.rebatePct}%` : ""}</span></div>
                            </div>
                          </div>

                          {/* ④ STAFF NOTES — wide */}
                          <div className="lg:col-span-4 rounded-lg border border-white/[0.06] p-2.5 flex flex-col" data-testid={`view-quotes-notes-col-${q.QuoteRef}`}>
                            <div data-testid={`view-quotes-notes-${q.QuoteRef}`}>
                              <div className="flex items-center text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-1.5">Staff notes<span className="ml-1.5 text-slate-600">{notes.length}</span>
                                <Link to={`/quotes/${id}/edit`} className="ml-auto normal-case tracking-normal text-[10.5px] text-teal-300 hover:underline" data-testid={`view-quotes-notes-add-${q.QuoteRef}`}>Add in quote →</Link></div>
                              {notes.length === 0 && <div className="text-[11px] text-slate-500">No staff notes</div>}
                              <div className="max-h-[340px] overflow-y-auto pr-1 space-y-1.5">
                                {[...notes].reverse().map((n, i) => (
                                  <div key={i} className="rounded-md bg-white/[0.025] border border-white/[0.05] px-2 py-1.5" data-testid={`view-quotes-note-${q.QuoteRef}-${i}`}>
                                    <div className="text-[12px] text-slate-200 whitespace-pre-wrap break-words leading-relaxed">{n.text || n.body || String(n)}</div>
                                    {n.by && <div className="text-[9.5px] text-slate-500 font-mono mt-0.5">{n.by}{n.ts ? ` · ${relTime(n.ts)}` : ""}</div>}
                                  </div>
                                ))}
                              </div>
                              {sysNotes.length > 0 && (
                                <div className="mt-2 pt-2 border-t border-white/[0.06]">
                                  <button onClick={() => setSysLog((p) => ({ ...p, [id]: !p[id] }))} data-testid={`view-quotes-syslog-toggle-${q.QuoteRef}`}
                                    className="text-[10px] font-mono text-slate-500 hover:text-slate-300 inline-flex items-center gap-1">
                                    {sysLog[id] ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />} {sysLog[id] ? "Hide" : "Show"} system log ({sysNotes.length})
                                  </button>
                                  {sysLog[id] && sysNotes.map((n, i) => (
                                    <div key={i} className="text-[10px] text-slate-500 font-mono whitespace-pre-wrap mt-1 pt-1 border-t border-white/[0.03]">{(n.body || n.text || "").slice(0, 400)}</div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <Person360Overlay personId={person360Id} onClose={() => setPerson360Id(null)} />
      {binOpen && <QuoteBinDrawer initialSearch={searchParams.get("bin") || ""} onClose={() => setBinOpen(false)} onRestored={load} />}
      {dupesOpen && <DuplicateQuotesDrawer onClose={() => setDupesOpen(false)} onChanged={load} />}
    </Layout>
  );
}
